import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaTruck,
    FaRoute,
    FaPlay,
    FaChevronRight,
    FaCheckCircle
} from "react-icons/fa";
import { loadsResource, reportResource } from "../../helpers/configRoutes/backend_helper";
import { useSelector } from "react-redux";

const Home = () => {
    const { user } = useSelector(state => state.Login);
    const navigate = useNavigate();
    const [readyLoads, setReadyLoads] = useState([]);
    const [recentCompletedLoad, setRecentCompletedLoad] = useState(null);
    const [stats, setStats] = useState({ active: 0, pending: 0, issues: 0 });

    const isLoadCompleted = (load) => load.status_id === 29
        || (load.stops?.length > 0 && load.stops.every((stop) => stop.status_operator_id === 33));

    const getActionableStop = (load) => load.stops?.find(
        (stop) => stop.can?.arrive || stop.can?.depart || stop.can?.complete
    ) || null;

    useEffect(() => {
        const fetchHomeData = async () => {
            const [loadsResponse, reportsResponse] = await Promise.all([
                loadsResource("get", { params: { perPage: 100 } }),
                reportResource("get", { params: { perPage: 100 } }),
            ]);

            const allLoads = loadsResponse.loads || [];
            const allReports = reportsResponse.reports || [];
            const activeLoads = allLoads.filter((load) => !isLoadCompleted(load));
            const actionableLoads = activeLoads.filter((load) => getActionableStop(load));
            const completedLoads = allLoads.filter((load) => isLoadCompleted(load));
            const openAlerts = allReports.filter((report) => !report.service_order_id);

            setReadyLoads(actionableLoads);
            setRecentCompletedLoad(completedLoads[0] || null);
            setStats({
                active: activeLoads.length,
                pending: actionableLoads.length,
                issues: openAlerts.length,
            });
        };

        fetchHomeData();
    }, []);

    return (
        <div className="min-h-screen bg-cs-bg-alt pb-24 font-sans">
            <header className="bg-white px-6 pt-12 pb-8 rounded-b-[3rem] shadow-soft">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="text-cs-muted text-xs font-bold uppercase tracking-widest">Welcome back,</p>
                        <h1 className="text-2xl font-black text-cs-dark tracking-tight">{user.name}</h1>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-cs-primary flex items-center justify-center text-white shadow-lg shadow-cs-primary/30">
                        <FaTruck size={20} />
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-cs-bg-alt p-3 rounded-2xl border border-cs-border/50 text-center">
                        <p className="text-[10px] font-bold text-cs-muted uppercase">Active</p>
                        <p className="text-lg font-black text-cs-dark">{stats.active}</p>
                    </div>
                    <div className="bg-cs-primary-soft p-3 rounded-2xl border border-cs-primary/10 text-center">
                        <p className="text-[10px] font-bold text-cs-primary uppercase">Ready</p>
                        <p className="text-lg font-black text-cs-primary">{stats.pending}</p>
                    </div>
                    <div className="bg-cs-bg-alt p-3 rounded-2xl border border-cs-border/50 text-center">
                        <p className="text-[10px] font-bold text-cs-muted uppercase">Alerts</p>
                        <p className="text-lg font-black text-cs-danger">{stats.issues}</p>
                    </div>
                </div>
            </header>

            <div className="p-6 space-y-8">
                <section>
                    <div className="flex items-center justify-between mb-4 px-1">
                        <h3 className="text-sm font-bold text-cs-dark uppercase tracking-widest">Ready to Work</h3>
                        <span className="text-[10px] font-bold text-cs-primary animate-pulse">LIVE NOW</span>
                    </div>

                    {readyLoads.length > 0 ? (
                        <div className="space-y-4">
                            {readyLoads.map((load) => {
                                const nextStop = getActionableStop(load) || load.first_stop;
                                const completedStops = load.stops?.filter((stop) => stop.status_operator_id === 33).length || 0;
                                const canStartJourney = completedStops === 0 && nextStop?.can?.arrive;

                                return (
                                    <div
                                        key={load.id}
                                        onClick={() => navigate(`/active-load/${load.id}`)}
                                        className="bg-white rounded-[2.5rem] p-6 shadow-soft border-2 border-cs-primary/20 relative overflow-hidden active:scale-[0.98] transition-all"
                                    >
                                        <div className="absolute top-0 right-0 p-4 opacity-[0.03]">
                                            <FaRoute size={100} />
                                        </div>

                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <span className="bg-cs-primary text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-md">
                                                    Load #{load.id}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-bold text-cs-muted uppercase">Distance</p>
                                                <p className="text-sm font-black text-cs-dark">{load.practical_miles} mi</p>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full bg-cs-primary" />
                                                <p className="text-sm font-bold text-cs-dark truncate">{load.first_stop?.name}</p>
                                            </div>
                                            <div className="w-[1px] h-4 bg-cs-border ml-1" />
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full border-2 border-cs-primary" />
                                                <p className="text-sm font-bold text-cs-dark truncate">{load.last_stop?.name}</p>
                                            </div>
                                        </div>

                                        <div className="mt-5 bg-cs-primary-soft rounded-2xl px-4 py-3 text-center">
                                            <p className="text-[10px] font-black text-cs-primary uppercase tracking-wide">Next stop</p>
                                            <p className="text-sm font-bold text-cs-dark mt-1 truncate">{nextStop?.name || 'Route in progress'}</p>
                                            <p className="text-[11px] text-cs-muted mt-2">{completedStops}/{load.stops?.length || 0} stops completed</p>
                                        </div>

                                        <button className="w-full mt-6 py-4 bg-cs-primary text-white font-black rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-cs-primary/30 group">
                                            <FaPlay size={12} className="group-hover:translate-x-1 transition-transform" />
                                            {canStartJourney ? 'START JOURNEY' : 'CONTINUE LOAD'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-cs-bg-alt rounded-[2rem] border-2 border-dashed border-cs-border p-10 text-center">
                            <p className="text-sm font-bold text-cs-muted italic">No loads ready right now</p>
                        </div>
                    )}
                </section>

                <section>
                    <h3 className="text-sm font-bold text-cs-dark uppercase tracking-widest mb-4 ml-1">Recent Activity</h3>
                    <div className="bg-white rounded-[2rem] shadow-soft overflow-hidden border border-white">
                        {recentCompletedLoad ? (
                            <div
                                onClick={() => navigate(`/active-load/${recentCompletedLoad.id}`)}
                                className="p-4 flex items-center justify-between border-b border-cs-bg-alt active:bg-cs-bg-alt transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-cs-success/10 text-cs-success flex items-center justify-center">
                                        <FaCheckCircle size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-cs-dark tracking-tight">Load #{recentCompletedLoad.id} Finished</p>
                                        <p className="text-[10px] font-bold text-cs-muted uppercase tracking-tighter">All stops completed</p>
                                    </div>
                                </div>
                                <FaChevronRight size={12} className="text-cs-border" />
                            </div>
                        ) : (
                            <div className="p-4 text-center">
                                <p className="text-sm font-bold text-cs-muted italic">No recent completed loads</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Home;
